import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/components/grc/shared";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Layers } from "lucide-react";

const PERIODS = [
  { key: "last30days", label: "30 days" },
  { key: "last90days", label: "90 days" },
  { key: "all", label: "All time" },
];

const BANDS = [
  { y: 20, label: "L1 Initial" },
  { y: 40, label: "L2 Developing" },
  { y: 60, label: "L3 Defined" },
  { y: 80, label: "L4 Managed" },
  { y: 100, label: "L5 Optimized" },
];

const PILLARS = Array.from({ length: 15 }, (_, i) => `P${i + 1}`);
const PILLAR_COLORS = [
  "#2563EB", "#DB2777", "#059669", "#D97706", "#7C3AED",
  "#DC2626", "#0891B2", "#65A30D", "#C026D3", "#EA580C",
  "#4F46E5", "#0D9488", "#B91C1C", "#525252", "#9333EA",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs space-y-1">
      <p className="font-semibold text-[#003366]">{fmtDateTime(d.date)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.stroke }}>
          {p.name}: <b>{p.value != null ? Number(p.value).toFixed(1) : "—"}%</b>
        </p>
      ))}
      {d.delta != null && (
        <p className={d.delta >= 0 ? "text-green-600" : "text-red-600"}>
          Δ {d.delta >= 0 ? "+" : ""}{d.delta.toFixed(1)} vs previous
        </p>
      )}
      <p className="text-slate-400 font-mono text-[10px]">trigger: {d.trigger}</p>
    </div>
  );
};

export const ComplianceTrend = ({ assessmentId }) => {
  const [period, setPeriod] = useState("all");
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overlays, setOverlays] = useState([]);

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/grc/${assessmentId}/trend`, { params: { period } });
      setSnapshots(data.snapshots || []);
    } catch {
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentId, period]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  const chartData = useMemo(
    () =>
      snapshots.map((s, i) => ({
        date: s.date,
        label: fmtDate(s.date),
        overall: s.overall_score,
        delta: i > 0 && s.overall_score != null && snapshots[i - 1].overall_score != null
          ? s.overall_score - snapshots[i - 1].overall_score
          : null,
        trigger: s.trigger,
        ...Object.fromEntries(overlays.map((p) => [p, s.pillar_scores?.[p]])),
      })),
    [snapshots, overlays]
  );

  const toggleOverlay = (p) =>
    setOverlays((o) => (o.includes(p) ? o.filter((x) => x !== p) : [...o, p]));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 space-y-4" data-testid="compliance-trend">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5" data-testid="trend-period-chips">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`trend-period-${p.key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                period === p.key
                  ? "bg-[#003366] text-white border-[#003366]"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto h-8 text-xs" data-testid="trend-pillar-overlay-button">
              <Layers size={13} className="mr-1.5" />
              Pillar overlays {overlays.length > 0 && `(${overlays.length})`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 max-h-72 overflow-y-auto" align="end">
            <p className="text-[11px] font-semibold text-slate-500 mb-2">Overlay pillar scores</p>
            <div className="space-y-1.5">
              {PILLARS.map((p, i) => (
                <label key={p} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`trend-overlay-${p}`}>
                  <Checkbox checked={overlays.includes(p)} onCheckedChange={() => toggleOverlay(p)} />
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PILLAR_COLORS[i] }} />
                  {p}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {loading ? (
        <Skeleton className="h-80 rounded-xl" />
      ) : snapshots.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500" data-testid="trend-empty-state">
          No snapshots in this period. Retest a control or resync from App A to record one.
        </div>
      ) : (
        <div className="h-80" data-testid="trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 90, bottom: 5, left: -15 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontSize: 10, fill: "#64748B" }} />
              <Tooltip content={<CustomTooltip />} />
              {overlays.length > 0 && <Legend wrapperStyle={{ fontSize: 10 }} />}
              {BANDS.map((b) => (
                <ReferenceLine
                  key={b.y}
                  y={b.y}
                  stroke="#CBD5E1"
                  strokeDasharray="4 4"
                  label={{ value: b.label, position: "right", fontSize: 9, fill: "#94A3B8" }}
                />
              ))}
              <Line
                type="monotone"
                dataKey="overall"
                name="Overall"
                stroke="#008080"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#008080" }}
                isAnimationActive={false}
              />
              {overlays.map((p) => (
                <Line
                  key={p}
                  type="monotone"
                  dataKey={p}
                  name={p}
                  stroke={PILLAR_COLORS[PILLARS.indexOf(p)]}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
